import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../Services/Store";
import styles from "./SaleOrderDetail.module.css";
import WebService from "../Services/WebService";
import CapsuleTabs from "./CapsuleTabs";
import BillingDocuments from "./BillingDocuments";
import {
  downloadReceiptPdf,
  generateReceiptForPayment,
  type InvoiceDoc,
  type ReceiptDoc,
} from "../Services/billing";
import { FaCheckCircle, FaFileDownload, FaReceipt } from "react-icons/fa";
import { toast } from "react-toastify";

interface PaymentHistoryItem {
  id?: number;
  amount_paid?: number | null;
  paid_at?: string | null;
  payment_method?: string;
  paid_by?: string;
  received_by?: string;
  label?: string;
  note?: string;
  reference?: string;
  receipt_url?: string;
  receipt_id?: number;
  receipt_number?: string;
  invoice_id?: number;
}

interface PaymentDetails {
  total_paid_amount?: number | null;
  due_amount?: number | null;
  payment_history: PaymentHistoryItem[];
}

interface OrderData {
  sale_order_id: number;
  customer_id: number | string;
  username: string;
  status: string;
  order_date?: string;
  created_at?: string;
  total_amount?: number | null;
  final_amount?: number | null;
  due_amount?: number | null;
  paid_amount?: number | null;
  retailer_type?: string;
  payment_details?: PaymentDetails;
  invoice?: InvoiceDoc | null;
  invoice_id?: number | null;
  receipts?: ReceiptDoc[];
}

const ORDER_STAGES = [
  { key: "CHECKOUT", label: "Order Confirmed" },
  { key: "PLACED", label: "Manufacturing Started" },
  { key: "IN_PROCESSING", label: "Manufacturing In Progress" },
  { key: "SHIPPED", label: "Shipped" },
  { key: "COMPLETED", label: "Complete" },
];

const currency = (amt?: number | null) =>
  typeof amt === "number" && !isNaN(amt)
    ? "₹" + amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })
    : "₹0.00";

const getCurrentStageIdx = (status: string) =>
  ORDER_STAGES.findIndex((s) => s.key === status);

const formatDateTime = (dateStr?: string | null) =>
  dateStr
    ? new Date(dateStr).toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "--";

const TrackPayment: React.FC = () => {
  const { orderId, id } = useParams<{ orderId?: string; id?: string }>();
  const user = useSelector((state: RootState) => state.auth.user);
  const saleOrderId = orderId || id;

  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [receiptBusyId, setReceiptBusyId] = useState<number | null>(null);

  const userType = (user?.user_type ?? user?.role_name ?? "").toString().toUpperCase();
  const isCustomer = userType === "CUSTOMER";
  const isStaff = userType === "ADMIN" || userType === "EMPLOYEE";

  const loadOrder = useCallback(() => {
    if (!saleOrderId) return;
    setLoading(true);
    const endpoint = isCustomer
      ? `/v1/door-management/saleorders/my-saleorders/${saleOrderId}`
      : `/v1/door-management/saleorders/${saleOrderId}`;

    WebService.getAPI<any>({ action: endpoint })
      .then((res) => {
        if (res?.status_code === 200 && res.data) {
          setOrderData(res.data);
        } else {
          setOrderData(null);
        }
      })
      .catch(() => setOrderData(null))
      .finally(() => setLoading(false));
  }, [saleOrderId, isCustomer]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const receipts = orderData?.receipts || [];
  const receiptById = new Map(
    receipts.filter((r) => r.receipt_id != null).map((r) => [r.receipt_id!, r])
  );

  const downloadReceipt = async (payment: PaymentHistoryItem) => {
    try {
      if (payment.receipt_id) {
        const meta = receiptById.get(payment.receipt_id);
        await downloadReceiptPdf({
          receiptId: payment.receipt_id,
          receiptNumber: meta?.receipt_number,
        });
        toast.success("Receipt downloaded.");
        return;
      }
      if (payment.receipt_url) {
        window.open(payment.receipt_url, "_blank", "noopener,noreferrer");
      }
    } catch {
      /* WebService already toasts */
    }
  };

  const handleGenerateReceipt = async (payment: PaymentHistoryItem) => {
    if (!payment.id) return;
    try {
      setReceiptBusyId(payment.id);
      const res = await generateReceiptForPayment(payment.id);
      toast.success(
        res?.data?.receipt_number
          ? `Receipt ${res.data.receipt_number} generated`
          : res?.message || "Receipt generated"
      );
      loadOrder();
    } catch {
      /* handled */
    } finally {
      setReceiptBusyId(null);
    }
  };

  const toNumber = (v: unknown): number => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
    return 0;
  };

  const paymentDetails = orderData?.payment_details;
  const paymentHistory = paymentDetails?.payment_history || [];
  const finalAmount = toNumber(orderData?.final_amount ?? orderData?.total_amount);
  const totalPaid = toNumber(paymentDetails?.total_paid_amount ?? orderData?.paid_amount);
  const dueAmount = toNumber(
    paymentDetails?.due_amount ??
      orderData?.due_amount ??
      Math.max(0, finalAmount - totalPaid)
  );
  const orderStatus = orderData?.status;
  const currentStageIdx = getCurrentStageIdx(orderStatus ?? "");

  return (
    <div className={styles.detailWrapper}>
      <CapsuleTabs />

      <div
        className={styles.orderBox}
        style={{
          marginBottom: 28,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 24, color: "#111827", marginBottom: 14 }}>
              Order Details
            </div>
            <div style={{ color: "#4B5563", fontSize: 14, marginBottom: 2 }}>
              Order No:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                #ORD-{orderData?.sale_order_id || "--"}
              </span>
            </div>
            <div style={{ color: "#4B5563", fontSize: 14, marginBottom: 2 }}>
              Customer ID:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                CUST-{orderData?.customer_id || "--"}
              </span>
            </div>
            <div style={{ color: "#4B5563", fontSize: 14 }}>
              Customer name:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                {orderData?.username || "--"}
              </span>
            </div>
          </div>
          <div style={{ minWidth: 200, textAlign: "right" }}>
            <div style={{ color: "#4B5563", fontSize: 14 }}>
              Order Date:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                {orderData?.order_date || orderData?.created_at
                  ? new Date(orderData?.order_date || orderData?.created_at!).toLocaleDateString("en-IN")
                  : "--"}
              </span>
            </div>
            <div style={{ color: "#111827", fontWeight: 700, fontSize: 18, margin: "12px 0 0" }}>
              Total Amount: {currency(finalAmount)}
            </div>
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 18, color: "#111827", marginBottom: 16 }}>
            Order Progress
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            {ORDER_STAGES.map((stage, idx) => {
              let circleBg = "#E5E7EB";
              let icon = <FaCheckCircle color="#fff" size={20} />;
              let textColor = "#9CA3AF";
              let barColor = "#E5E7EB";
              if (idx < currentStageIdx) {
                circleBg = "#10B981";
                textColor = "#111827";
                barColor = "#10B981";
              }
              if (idx === currentStageIdx) {
                circleBg = "#3B82F6";
                textColor = "#111827";
                barColor = "#3B82F6";
              }
              return (
                <React.Fragment key={stage.key}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 110 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: circleBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 4,
                      }}
                    >
                      {icon}
                    </div>
                    <div
                      style={{
                        color: textColor,
                        fontWeight: 400,
                        fontSize: 14,
                        textAlign: "center",
                        marginTop: 4,
                      }}
                    >
                      {stage.label}
                    </div>
                  </div>
                  {idx < ORDER_STAGES.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: 4,
                        background: barColor,
                        minWidth: 40,
                        maxWidth: 300,
                        margin: "0 2px",
                        borderRadius: 99,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {orderData && (
        <div
          className={styles.orderBox}
          style={{
            marginBottom: 28,
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 20, color: "#111827", marginBottom: 16 }}>
            Invoice & Receipts
          </div>
          <BillingDocuments
            saleOrderId={orderData.sale_order_id}
            invoice={orderData.invoice}
            invoiceId={orderData.invoice_id}
            receipts={orderData.receipts}
            isCustomer={isCustomer}
          />
        </div>
      )}

      <div
        className={styles.orderBox}
        style={{
          marginBottom: 28,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 600, fontSize: 24, color: "#111827", marginBottom: 12 }}>
            Payment Details
          </div>
          <div
            style={{
              background: "#DBEAFE",
              borderRadius: 99,
              color: "#1E40AF",
              fontSize: 14,
              fontWeight: 500,
              padding: "6px 18px",
              marginLeft: 18,
              marginBottom: 4,
            }}
          >
            Order ID: #ORD-{orderData?.sale_order_id || "--"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 46, marginTop: 30, marginLeft: 12, flexWrap: "wrap" }}>
          <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "16px 18px", minWidth: 150 }}>
            <div style={{ color: "#4B5563", fontSize: 14, fontWeight: 400, marginBottom: 6 }}>Total Amount</div>
            <div style={{ color: "#111827", fontWeight: 700, fontSize: 24 }}>{currency(finalAmount)}</div>
          </div>
          <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "16px 18px", minWidth: 150 }}>
            <div style={{ color: "#4B5563", fontSize: 14, fontWeight: 400, marginBottom: 6 }}>Amount Paid</div>
            <div style={{ color: "#059669", fontWeight: 700, fontSize: 24 }}>{currency(totalPaid)}</div>
          </div>
          <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "16px 18px", minWidth: 150 }}>
            <div style={{ color: "#4B5563", fontSize: 14, fontWeight: 400, marginBottom: 6 }}>Due Amount</div>
            <div style={{ color: "#DC2626", fontWeight: 700, fontSize: 24 }}>{currency(dueAmount)}</div>
          </div>
        </div>
      </div>

      <div
        className={styles.orderBox}
        style={{
          minHeight: 340,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 20, color: "#111827", marginBottom: 18 }}>
          Payment Timeline
        </div>
        {paymentHistory.length === 0 ? (
          <div style={{ color: "#7e7e7e", fontSize: 16, padding: 36 }}>No payments yet.</div>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-start", position: "relative", marginLeft: 12 }}>
            <div
              style={{
                width: 2,
                minHeight: 200,
                background: "#10B981",
                position: "absolute",
                top: 12,
                left: 32,
                zIndex: 0,
                height: "90%",
              }}
            />
            <div style={{ width: "100%" }}>
              {paymentHistory.map((payment, idx) => {
                const receiptMeta = payment.receipt_id ? receiptById.get(payment.receipt_id) : undefined;
                return (
                  <div
                    key={payment.id ?? idx}
                    style={{ display: "flex", alignItems: "flex-start", marginBottom: 24, position: "relative" }}
                  >
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        background: "#D1FAE5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: 24,
                        zIndex: 1,
                      }}
                    >
                      <FaCheckCircle size={32} color="#059669" />
                    </div>
                    <div
                      style={{
                        minWidth: 250,
                        background: "#fff",
                        borderRadius: 8,
                        outline: "1px solid #E5E7EB",
                        padding: 17,
                        boxSizing: "border-box",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", marginBottom: 7 }}>
                        <span style={{ color: "#6B7280", fontSize: 14, marginRight: 7 }}>
                          {formatDateTime(payment.paid_at)}
                        </span>
                      </div>
                      <div style={{ color: "#111827", fontWeight: 500, fontSize: 18, marginBottom: 6 }}>
                        {payment.label || "Payment received"}
                      </div>
                      <div style={{ color: "#4B5563", fontSize: 16, marginBottom: 2 }}>
                        Amount Paid: {currency(payment.amount_paid)}
                      </div>
                      {payment.payment_method && (
                        <div style={{ color: "#4B5563", fontSize: 16, marginBottom: 2 }}>
                          Payment Method: {payment.payment_method}
                        </div>
                      )}
                      {(receiptMeta?.receipt_number || payment.receipt_number || payment.receipt_id) && (
                        <div style={{ color: "#059669", fontSize: 15, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                          <FaReceipt />
                          {receiptMeta?.receipt_number || payment.receipt_number || `Receipt #${payment.receipt_id}`}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
                        {(payment.receipt_id || payment.receipt_url) && (
                          <button
                            type="button"
                            onClick={() => downloadReceipt(payment)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 7,
                              background: "#fff",
                              borderRadius: 8,
                              outline: "1px solid #D1D5DB",
                              border: "none",
                              color: "#374151",
                              fontSize: 15,
                              fontWeight: 400,
                              padding: "8px 18px",
                              cursor: "pointer",
                            }}
                          >
                            <FaFileDownload /> Download Receipt
                          </button>
                        )}
                        {isStaff && payment.id && !payment.receipt_id && (
                          <button
                            type="button"
                            onClick={() => handleGenerateReceipt(payment)}
                            disabled={receiptBusyId === payment.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 7,
                              background: "#FFD400",
                              borderRadius: 8,
                              border: "none",
                              color: "#111",
                              fontSize: 15,
                              fontWeight: 600,
                              padding: "8px 18px",
                              cursor: "pointer",
                            }}
                          >
                            <FaReceipt />
                            {receiptBusyId === payment.id ? "Generating…" : "Generate Receipt"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10,
            background: "rgba(255,255,255,0.33)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: "#FFD400", fontWeight: 700, fontSize: 20 }}>Loading...</span>
        </div>
      )}
    </div>
  );
};

export default TrackPayment;
